import "./App.css";
import { Route, Routes, useLocation } from "react-router";
import { Suspense, lazy, useState, useEffect } from "react";
import CustomCursor from "./components/ui/CustomCursor/CustomCursor";
import { CursorProvider } from "./context/CursorContext";
import { CaseStudy } from "./pages/CaseStudy/CaseStudy";
import { LoadingOverlay } from "./components/ui/LoadingOverlay/LoadingOverlay";
import { LoadingProvider } from "./context/LoadingContext";
import Impact from "./components/Impact/Impact";
import { Navbar } from "./components/ui/Navbar/Navbar";
import Footer from "./components/Footer/Footer";
import { usePageTracking } from "./hooks/usePageTracking";
import { AboutUs } from "./pages/AboutUs";

// Minimum loading times (in milliseconds)
const MIN_INITIAL_LOAD_TIME = 800; // 1.5s minimum for initial page load
const MIN_ROUTE_CHANGE_TIME = 700; // 0.8s minimum for route changes

// Lazy load components
const Landing = lazy(() =>
  import("./pages/Landing").then((module) => ({ default: module.Landing })),
);
// const KnowUs = lazy(() => import("./pages/KnowUs"));
// const MotionProjects = lazy(() =>
//   import("./pages/MotionProjects").then((module) => ({
//     default: module.default,
//   })),
// );
const ProjectDetails = lazy(() =>
  import("./pages/ProjectDetails").then((module) => ({
    default: module.default,
  })),
);

function App() {
  usePageTracking();

  const [isLoading, setIsLoading] = useState(true); // change to true before commit
  const [pageShown, setPageShown] = useState(false);
  const location = useLocation();
  function handlePostLoad() {
    setIsLoading(false);
    setTimeout(() => {
      setPageShown(true);
    }, 1000);
  }
  useEffect(() => {
    // Function to wait for minimum time
    const minTimePromise = new Promise<void>((resolve) => {
      setTimeout(() => resolve(), MIN_INITIAL_LOAD_TIME);
    });

    // Function to wait for actual load
    const loadPromise = new Promise<void>((resolve) => {
      if (document.readyState === "complete") {
        resolve();
      } else {
        const handleLoad = () => resolve();
        window.addEventListener("load", handleLoad, { once: true });
      }
    });

    // Wait for both minimum time and actual load
    Promise.all([minTimePromise, loadPromise]).then(() => handlePostLoad());
  }, []); // Initial load

  useEffect(() => {
    if (location.pathname === window.location.pathname) return; // Skip initial render

    setIsLoading(true);

    // Function to wait for minimum time
    const minTimePromise = new Promise<void>((resolve) => {
      setTimeout(() => resolve(), MIN_ROUTE_CHANGE_TIME);
    });

    // Function to check for content load
    const contentLoadPromise = new Promise<void>((resolve) => {
      const checkContent = () => {
        const mainContent = document.querySelector("main");
        if (mainContent && mainContent.children.length > 0) {
          resolve();
        } else {
          requestAnimationFrame(checkContent);
        }
      };
      requestAnimationFrame(checkContent);
    });

    // Wait for both minimum time and content load
    Promise.all([minTimePromise, contentLoadPromise]).then(() =>
      handlePostLoad(),
    );
  }, [location.pathname]); // Trigger on route changes

  return (
    <CursorProvider>
      <LoadingProvider isLoading={!pageShown}>
        <CustomCursor />
        <LoadingOverlay
          isLoading={isLoading}
          barColor="red"
          backgroundColor="rgba(0, 0, 0, 1)"
        />
        <div className="relative">
          <Navbar />
          <main>
            <Suspense fallback={null}>
              <Routes>
                <Route path="/" element={<Landing />} />
                {/* <Route
                  path="/projects"
                  element={
                    <MotionProjects
                      onNavbarVisibilityChange={setNavbarVisible}
                    />
                  }
                /> */}
                <Route path="/project-details" element={<ProjectDetails />} />
                <Route path="/case-study/:projectId" element={<CaseStudy />} />
                <Route path="/impact" element={<Impact />} />
                <Route path="/about-us" element={<AboutUs />} />
                {/* <Route path="/know-us" element={<KnowUs />} /> */}
              </Routes>
            </Suspense>
          </main>
          <Footer />
        </div>
      </LoadingProvider>
    </CursorProvider>
  );
}
export default App;
